import { useEffect, useRef } from 'react'
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
import { generatePayload } from '../api/authApi'
import { savePayload } from '../shared/utils/tonProofPayload'
import { isAuthenticated } from '../shared/utils/authToken'

/**
 * Hook to configure TON Connect with proof support
 * This must be called early in the app lifecycle (before wallet connection)
 */
export function useTonProofSetup(): void {
  const [tonConnectUI] = useTonConnectUI()
  const wallet = useTonWallet()
  const isSetupRef = useRef(false)

  // Reset setup flag when wallet disconnects
  // This ensures we generate new payload on reconnection
  useEffect(() => {
    if (!wallet?.account && isSetupRef.current) {
      console.log('Authentication: Wallet disconnected, resetting setup flag')
      isSetupRef.current = false
    }
  }, [wallet?.account])

  useEffect(() => {
    // Run setup only once (until wallet disconnects)
    if (isSetupRef.current) {
      return
    }

    const setupTonProof = async () => {
      try {
        // If wallet is already connected and authenticated, skip payload generation
        // This prevents unnecessary API calls and payload mismatch after page reload
        if (wallet?.account) {
          const walletAddress = wallet.account.address
          if (isAuthenticated(walletAddress)) {
            // User is already authenticated, no need to generate payload
            isSetupRef.current = true
            return
          }
        }

        // Generate payload from backend
        const { payload, payloadToken } = await generatePayload()

        // Save payload for later verification
        savePayload(payload, payloadToken)

        // Configure TON Connect to request proof with this payload
        tonConnectUI.setConnectRequestParameters({
          state: 'ready',
          value: {
            tonProof: payload,
          },
        })

        isSetupRef.current = true
      } catch (error) {
        // On error, set to ready state without tonProof
        // This allows users to connect without proof if backend is unavailable
        console.error('Authentication: Failed to setup TON proof:', error)
        tonConnectUI.setConnectRequestParameters({
          state: 'ready',
          value: {},
        })
      }
    }

    setupTonProof()
  }, [tonConnectUI, wallet])

  // Refresh payload periodically (every 10 minutes) to keep it fresh
  // Only refresh if wallet is NOT connected or NOT authenticated
  useEffect(() => {
    // Don't refresh payload if wallet is connected and authenticated
    if (wallet?.account) {
      const walletAddress = wallet.account.address
      if (isAuthenticated(walletAddress)) {
        return
      }
    }

    const refreshInterval = setInterval(
      async () => {
        try {
          // Double-check authentication before making API call
          if (wallet?.account && isAuthenticated(wallet.account.address)) {
            return
          }

          const { payload, payloadToken } = await generatePayload()
          savePayload(payload, payloadToken)
          tonConnectUI.setConnectRequestParameters({
            state: 'ready',
            value: {
              tonProof: payload,
            },
          })
        } catch (error) {
          console.error(
            'Authentication: Failed to refresh TON proof payload:',
            error,
          )
        }
      },
      10 * 60 * 1000,
    ) // 10 minutes

    return () => clearInterval(refreshInterval)
  }, [tonConnectUI, wallet])
}
